import { ReactNode, useState } from 'react'
import { Loader2 } from '@digital-twins/design-system/icons'
import { defaultUrlTransform } from 'react-markdown'
import { useTranslation } from 'react-i18next'
import { KnowledgeAskCitation, KnowledgeDocumentListItem } from '@digital-twins/shared-types'

import { CreativeLightbox } from '@/components/chat/CreativeLightbox'
import { CreativeMedia, isVideoCreativeLike } from '@/components/chat/CreativeMedia'
import { ThreadAssetView } from '@/lib/api'
import { documentTitle } from '@/pages/KnowledgeHubPage/libraryHelpers'

import { ThreadDocChip } from './ThreadDocChip'
import { usePrivateMedia } from '@/hooks/usePrivateMedia'

const CREATIVE_REF = /^(asset|doc):[A-Za-z0-9_-]{1,64}$/

export interface CreativeMarkdownImageProps {
  src?: string
  alt?: string
}

export interface CreativeMarkdownLinkProps {
  href?: string
  children?: ReactNode
}

export interface CreativeMarkdownHandlers {
  urlTransform: (url: string) => string
  renderImage: (props: CreativeMarkdownImageProps) => ReactNode
  renderLink: (props: CreativeMarkdownLinkProps) => ReactNode
}

interface CreateCreativeMarkdownHandlersArgs {
  assets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  onOpenCitation?: (index: number, citation: KnowledgeAskCitation) => void
}

interface AssetInlineImageProps {
  src: string
  mimeType?: string
  alt?: string
  token: string
}

/**
 * An inline image asset: opens the lightbox (with download) on click, and an
 * image URL that will not load falls back to its alt text, never a broken icon.
 */
function AssetInlineImage(props: AssetInlineImageProps) {
  const { src, mimeType, alt, token } = props

  const { t } = useTranslation('app')
  const [hasFailed, setHasFailed] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const media = usePrivateMedia(src)

  if (hasFailed || media.error) {
    return alt ? <>{alt}</> : null
  }

  if (!media.src) {
    return <span role="status" aria-label={t('creativeLibrary.loading')}
      className="inline-flex h-40 w-64 max-w-full items-center justify-center rounded-lg border border-border bg-background">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground motion-reduce:animate-none" aria-hidden />
    </span>
  }

  const lightbox = isOpen ? (
    <CreativeLightbox
      creatives={[{ src, mimeType }]}
      index={0}
      onIndex={() => {}}
      onClose={() => setIsOpen(false)}
    />
  ) : null

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={alt || t('chromeA11y.fullCreative')}
        data-testid={`creative-markdown-media-open-${token}`}
        className="inline-block w-fit max-w-full cursor-zoom-in rounded-lg border-0 bg-transparent p-0 align-top focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:max-w-md"
      >
        <img
          src={media.src ?? undefined}
          alt={alt ?? t('chatChrome.creativeAlt')}
          onError={() => setHasFailed(true)}
          data-testid={`creative-markdown-media-${token}`}
          className="h-auto max-h-80 w-auto max-w-full rounded-lg object-contain sm:max-h-96"
        />
      </button>
      {lightbox}
    </>
  )
}

/** Extends react-markdown's default so `asset:` / `doc:` refs survive. */
export function creativeUrlTransform(url: string) {
  return CREATIVE_REF.test(url) ? url : defaultUrlTransform(url)
}

function parseCreativeRef(
  url: string | undefined,
): { scheme: 'asset' | 'doc'; token: string } | null {
  if (!url || !CREATIVE_REF.test(url)) {
    return null
  }
  const separator = url.indexOf(':')
  const scheme = url.slice(0, separator)
  const token = url.slice(separator + 1)
  if (scheme !== 'asset' && scheme !== 'doc') {
    return null
  }
  return { scheme, token }
}

function nodeText(children: ReactNode): string {
  if (children == null || typeof children === 'boolean') {
    return ''
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children)
  }
  if (Array.isArray(children)) {
    return children.map(nodeText).join('')
  }
  return ''
}

function citationFromDocument(
  doc: KnowledgeDocumentListItem,
  label: string,
): KnowledgeAskCitation {
  return {
    documentName: doc.originalFilename || label || doc.token,
    documentToken: doc.token,
    snippet: '',
    granularity: 'document',
    extractionVersion: 'v0',
  }
}

/**
 * Resolves `asset:` / `doc:` markdown refs against the thread's asset list
 * and document index. Used by both chat preview and saved artifact cards.
 */
export function createCreativeMarkdownHandlers(
  args: CreateCreativeMarkdownHandlersArgs,
): CreativeMarkdownHandlers {
  const { assets = [], docsByToken, onOpenCitation } = args

  const assetsByToken = new Map(
    assets.map((asset) => [asset.token, asset]),
  )

  function renderImage(props: CreativeMarkdownImageProps): ReactNode {
    const { src, alt } = props
    const ref = parseCreativeRef(src)
    if (!ref) {
      return undefined
    }
    if (ref.scheme !== 'asset') {
      return alt || null
    }
    const asset = assetsByToken.get(ref.token)
    const publicUrl = asset?.ref.publicUrl
    if (!publicUrl) {
      return alt || null
    }
    if (!isVideoCreativeLike({ mimeType: asset.ref.mimeType, src: publicUrl })) {
      return (
        <AssetInlineImage
          src={publicUrl}
          mimeType={asset.ref.mimeType}
          alt={alt}
          token={ref.token}
        />
      )
    }
    return (
      <CreativeMedia
        src={publicUrl}
        mimeType={asset.ref.mimeType}
        alt={alt}
        className="max-w-full h-auto"
        dataTestId={`creative-markdown-media-${ref.token}`}
      />
    )
  }

  function renderLink(props: CreativeMarkdownLinkProps): ReactNode {
    const { href, children } = props
    const ref = parseCreativeRef(href)
    if (!ref) {
      return undefined
    }
    if (ref.scheme !== 'doc') {
      return children
    }
    const doc = docsByToken?.get(ref.token)
    if (!doc) {
      return children
    }
    const title = documentTitle(doc) || nodeText(children) || doc.token
    return (
      <ThreadDocChip
        title={title}
        documentToken={doc.token}
        onOpen={
          onOpenCitation
            ? () => {
                onOpenCitation(1, citationFromDocument(doc, title))
              }
            : undefined
        }
      />
    )
  }

  return {
    urlTransform: creativeUrlTransform,
    renderImage,
    renderLink,
  }
}
